import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import localeText from "../../../../../locale";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";

const CONSENT_TEXT =
  "I consent to the use of the information provided for a free assessment for the EXTRACONOMY platform and authorize the processing of data in accordance with current legislation and GDPR.";

const StepOtherInfo = ({ data = {}, meta = {}, onSubmit, onBack, onSendDiagnosis, saving }) => {
  const { showSuccessToast, showErrorToast } = useToastContext();
  const [social_media_presence, setSocialMediaPresence] = useState(false);
  const [e_commerce, setECommerce] = useState(false);
  const [gender_equality_certification, setGenderEqualityCertification] = useState(false);
  const [iso_certification, setIsoCertification] = useState({ present: false, which: "" });
  const [cyber_security, setCyberSecurity] = useState(false);
  const [gdpr_compliance, setGdprCompliance] = useState(false);
  const [use_of_subsidized_finance, setUseOfSubsidizedFinance] = useState(false);
  const [use_of_funded_training, setUseOfFundedTraining] = useState(false);
  const [has_liquidity_manager, setHasLiquidityManager] = useState(false);
  const [diversified_liquidity_with_gold, setDiversifiedLiquidityWithGold] = useState(false);
  const [consent, setConsent] = useState(!!meta.consent);

  useEffect(() => {
    setSocialMediaPresence(!!data.social_media_presence);
    setECommerce(!!data.e_commerce);
    setGenderEqualityCertification(!!data.gender_equality_certification);
    setIsoCertification(data.iso_certification ?? { present: false, which: "" });
    setCyberSecurity(!!data.cyber_security);
    setGdprCompliance(!!data.gdpr_compliance);
    setUseOfSubsidizedFinance(!!data.use_of_subsidized_finance);
    setUseOfFundedTraining(!!data.use_of_funded_training);
    setHasLiquidityManager(!!data.has_liquidity_manager);
    setDiversifiedLiquidityWithGold(!!data.diversified_liquidity_with_gold);
    setConsent(!!meta.consent);
  }, [data, meta.consent]);

  const handleSaveAndContinue = (e) => {
    e.preventDefault();
    onSubmit({
      social_media_presence,
      e_commerce,
      gender_equality_certification,
      iso_certification: {
        present: iso_certification.present,
        which: iso_certification.present ? iso_certification.which || null : null,
      },
      cyber_security,
      gdpr_compliance,
      use_of_subsidized_finance,
      use_of_funded_training,
      has_liquidity_manager,
      diversified_liquidity_with_gold,
      consent,
    });
  };

  const handleSendDiagnosis = (e) => {
    e.preventDefault();
    if (!consent) return;
    const promise = onSubmit({
      social_media_presence,
      e_commerce,
      gender_equality_certification,
      iso_certification: {
        present: iso_certification.present,
        which: iso_certification.present ? iso_certification.which || null : null,
      },
      cyber_security,
      gdpr_compliance,
      use_of_subsidized_finance,
      use_of_funded_training,
      has_liquidity_manager,
      diversified_liquidity_with_gold,
      consent: true,
    });
    if (promise && typeof promise.then === "function") {
      promise.then((submittedData) => onSendDiagnosis?.(submittedData));
      showSuccessToast({
        detail: "profileAnalysisSubmitSuccess" || "Diagnosi inviata con successo.",
      });
    } else {
      onSendDiagnosis?.();
      showErrorToast({
        code: 500000,
        detail: "businessDiagnosisSubmitError" || "Errore nell'invio della diagnosi.",
      });
    }
  };

  return (
    <div className="flex flex-column gap-3">
      <div>
        <h2 className="text-lg font-semibold m-0 mb-2" style={{ color: "var(--theme-text)" }}>
          {localeText("businessDiagnosisStep8Title") || "Altre info"}
        </h2>
        <p className="text-sm m-0 mb-2" style={{ color: "var(--theme-text-muted)" }}>
          {localeText("businessDiagnosisStep8Description") ||
            "Spiega come la tua azienda utilizza strumenti tecnologici e strategie innovative per rimanere competitiva."}
        </p>
      </div>
      <div className="p-fluid grid" style={{ rowGap: "1rem" }}>
        <div className="col-12 md:col-6 flex gap-2">
          <Checkbox
            inputId="social_media_presence"
            checked={social_media_presence}
            onChange={(e) => setSocialMediaPresence(e.checked)}
          />
          <label htmlFor="social_media_presence">
            <div className="font-medium text-sm" style={{ color: "var(--theme-text)" }}>
              {localeText("businessDiagnosisStep8LabelSocialMediaPresence") || "Presenza su social"}
            </div>
            <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("businessDiagnosisStep8LabelSocialMediaPresenceHelp") ||
                "Presenza su social"}
            </div>
          </label>
        </div>
        <div className="col-12 md:col-6 flex gap-2">
          <Checkbox
            inputId="e_commerce"
            checked={e_commerce}
            onChange={(e) => setECommerce(e.checked)}
          />
          <label htmlFor="e_commerce">
            <div className="font-medium text-sm" style={{ color: "var(--theme-text)" }}>
              {localeText("businessDiagnosisStep8LabelECommerce") || "E-commerce"}
            </div>
            <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("businessDiagnosisStep8LabelECommerceHelp") || "E-commerce"}
            </div>
          </label>
        </div>
        <div className="col-12 md:col-6 flex gap-2">
          <Checkbox
            inputId="gender_equality_certification"
            checked={gender_equality_certification}
            onChange={(e) => setGenderEqualityCertification(e.checked)}
          />
          <label htmlFor="gender_equality_certification">
            <div className="font-medium text-sm" style={{ color: "var(--theme-text)" }}>
              {localeText("businessDiagnosisStep8LabelGenderEqualityCertification") ||
                "Certificazione Parità di Genere"}
            </div>
            <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("businessDiagnosisStep8LabelGenderEqualityCertificationHelp") ||
                "Certificazione Parità di Genere"}
            </div>
          </label>
        </div>
        <div className="col-12">
          <div className="flex gap-2 mb-1">
            <Checkbox
              inputId="iso_certification"
              checked={iso_certification.present}
              onChange={(e) => setIsoCertification((p) => ({ ...p, present: e.checked }))}
            />
            <label htmlFor="iso_certification">
              <div className="font-medium text-sm" style={{ color: "var(--theme-text)" }}>
                {localeText("businessDiagnosisStep8LabelIso") || "Iso"}
              </div>
              <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
                {localeText("businessDiagnosisStep8LabelIsoHelp") || "Iso"}
              </div>
            </label>
          </div>
          {iso_certification.present && (
            <div className="col-12">
              <label className="block text-sm mb-1" style={{ color: "var(--theme-text)" }}>
                {localeText("businessDiagnosisStep8IsoWhichLabel") || "Indicare quali"}
              </label>
              <InputText
                value={iso_certification.which}
                onChange={(e) => setIsoCertification((p) => ({ ...p, which: e.target.value }))}
                className="w-full border-round-md p-no-outline"
              />
            </div>
          )}
        </div>
        <div className="col-12 md:col-6 flex gap-2">
          <Checkbox
            inputId="cyber_security"
            checked={cyber_security}
            onChange={(e) => setCyberSecurity(e.checked)}
          />
          <label htmlFor="cyber_security">
            <div className="font-medium text-sm" style={{ color: "var(--theme-text)" }}>
              {localeText("businessDiagnosisStep8LabelCyberSecurity") || "Cyber Sicurity"}
            </div>
            <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("businessDiagnosisStep8LabelCyberSecurityHelp") || "Cyber Sicurity"}
            </div>
          </label>
        </div>
        <div className="col-12 md:col-6 flex gap-2">
          <Checkbox
            inputId="gdpr_compliance"
            checked={gdpr_compliance}
            onChange={(e) => setGdprCompliance(e.checked)}
          />
          <label htmlFor="gdpr_compliance">
            <div className="font-medium text-sm" style={{ color: "var(--theme-text)" }}>
              {localeText("businessDiagnosisStep8LabelGdpr") || "DGPR"}
            </div>
            <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("businessDiagnosisStep8LabelGdprHelp") || "DGPR"}
            </div>
          </label>
        </div>
        <div className="col-12 md:col-6 flex gap-2">
          <Checkbox
            inputId="use_of_subsidized_finance"
            checked={use_of_subsidized_finance}
            onChange={(e) => setUseOfSubsidizedFinance(e.checked)}
          />
          <label htmlFor="use_of_subsidized_finance">
            <div className="font-medium text-sm" style={{ color: "var(--theme-text)" }}>
              {localeText("businessDiagnosisStep8LabelSubsidizedFinance") ||
                "Utilizzo di Finanza Agevolata"}
            </div>
            <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("businessDiagnosisStep8LabelSubsidizedFinanceHelp") ||
                "Utilizzo di Finanza Agevolata"}
            </div>
          </label>
        </div>
        <div className="col-12 md:col-6 flex gap-2">
          <Checkbox
            inputId="use_of_funded_training"
            checked={use_of_funded_training}
            onChange={(e) => setUseOfFundedTraining(e.checked)}
          />
          <label htmlFor="use_of_funded_training">
            <div className="font-medium text-sm" style={{ color: "var(--theme-text)" }}>
              {localeText("businessDiagnosisStep8LabelFundedTraining") ||
                "Utilizzo di Formazione Finanziata"}
            </div>
            <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("businessDiagnosisStep8LabelFundedTrainingHelp") ||
                "Utilizzo di Formazione Finanziata"}
            </div>
          </label>
        </div>
        <div className="col-12 md:col-6 flex gap-2">
          <Checkbox
            inputId="has_liquidity_manager"
            checked={has_liquidity_manager}
            onChange={(e) => setHasLiquidityManager(e.checked)}
          />
          <label htmlFor="has_liquidity_manager">
            <div className="font-medium text-sm" style={{ color: "var(--theme-text)" }}>
              {localeText("businessDiagnosisStep8LabelHasLiquidityManager") ||
                "Hai un gestore per la liquidità dell'azienda"}
            </div>
            <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("businessDiagnosisStep8LabelHasLiquidityManagerHelp") ||
                "Hai un gestore per la liquidità dell'azienda"}
            </div>
          </label>
        </div>
        <div className="col-12 md:col-6 flex gap-2">
          <Checkbox
            inputId="diversified_liquidity_with_gold"
            checked={diversified_liquidity_with_gold}
            onChange={(e) => setDiversifiedLiquidityWithGold(e.checked)}
          />
          <label htmlFor="diversified_liquidity_with_gold">
            <div className="font-medium text-sm" style={{ color: "var(--theme-text)" }}>
              {localeText("businessDiagnosisStep8LabelDiversifiedLiquidityWithGold") ||
                "Hai diversificato la liquidità dell'azienda acquistando oro"}
            </div>
            <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("businessDiagnosisStep8LabelDiversifiedLiquidityWithGoldHelp") ||
                "Hai diversificato la liquidità dell'azienda acquistando oro"}
            </div>
          </label>
        </div>
      </div>

      <div className="mt-3 pt-3 border-top-1 surface-border">
        <div className="grid">
          <div className="col-12 md:col-6">
            <span className="text-sm font-medium">
              {localeText("businessDiagnosisStep8LabelDate") || "Date: "}
            </span>
            <span className="text-sm">
              {meta.date || meta.created_at || localeText("businessDiagnosisNoData") || "—"}
            </span>
          </div>
          <div className="col-12 md:col-6">
            <span className="text-sm font-medium">
              {localeText("businessDiagnosisStep8LabelConsultant") || "Consultant: "}
            </span>
            <span className="text-sm">
              {meta.consultant ?? (localeText("businessDiagnosisNoData") || "—")}
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mt-2">
        <Checkbox inputId="consent" checked={consent} onChange={(e) => setConsent(e.checked)} />
        <label htmlFor="consent" className="text-sm" style={{ color: "var(--theme-text)" }}>
          <div className="font-medium text-sm" style={{ color: "var(--theme-text)" }}>
            {localeText("businessDiagnosisConsent") || "I consent"}
          </div>
          <div className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
            {localeText("businessDiagnosisConsentText") || CONSENT_TEXT}
          </div>
        </label>
      </div>

      <div className="flex gap-2 flex-wrap mt-2">
        {onBack && (
          <Button
            type="button"
            label={localeText("back") || "Indietro"}
            className="p-button-text"
            style={{ color: "var(--theme-primary)" }}
            onClick={onBack}
            disabled={saving}
          />
        )}

        <Button
          type="submit"
          loading={saving}
          disabled={saving || !consent}
          onClick={handleSendDiagnosis}
          className="border-round-md flex align-items-center gap-2 py-3 px-3"
        >
          <span className="text-sm text-white font-medium">
            {localeText("sendDiagnosis") || "Invia Diagnosi"}
          </span>
          <i className="pi pi-arrow-right text-white text-xs"></i>
        </Button>
      </div>
    </div>
  );
};

export default StepOtherInfo;
